import { useEffect, useCallback, useState, useContext } from 'react';
import { useParams, useLocation } from 'react-router';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useBaseHistory } from 'spindl-shared-components';

import { DropDownItemCombinedProps } from '../../../lib/types';
import { DatabasePageCombinedProps, DatabasePageParams } from './types';
import { getDatabaseTablesUseCase } from './DatabasePage.interactor';
import { TableStoreContext } from '../../../stores/TableStoreContext';

const usePresenter = (props: DatabasePageCombinedProps): DatabasePageCombinedProps => {
  const { t } = useTranslation();
  const history = useBaseHistory();
  const { pathname } = useLocation();
  const { databaseSlug, tableSlug } = useParams<DatabasePageParams>();
  const tableStore = useContext(TableStoreContext);

  const { data: tables, isLoading } = useQuery(['getAllDatabaseTables', databaseSlug], () =>
    getDatabaseTablesUseCase(databaseSlug),
  );

  const [query, setQuery] = useState<string>('');
  const [tabsAutoScrollEnabled, setTabsAutoScrollEnabled] = useState(true); // controls the auto-scrolling behaviour of the tableTabNav bar

  const routeToTable = (slug: string) => {
    if (tableSlug && tableSlug === slug) {
      return;
    }
    tableStore.resetStore();
    history.replace(`/database/${databaseSlug}/table/${slug}`);
  };

  useEffect(() => {
    if (tables && !pathname.includes('table')) {
      routeToTable(tables[0].slug);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  const routeToDatabaseHomepage = () => {
    history.push('/');
  };

  const getMappedMenuTabItems = useCallback(() => {
    if (tables) {
      return tables.map((table) => {
        const dropDownItem: DropDownItemCombinedProps = {
          text: {
            value: table.name,
          },
          state: 'Unselected',
          type: tableSlug && table.slug === tableSlug ? 'DefaultIconText' : 'DefaultText',
          onClick: () => {
            // reset search query for a new database 
            if (query) {
              setQuery('');
            }
            routeToTable(table.slug);
            setTabsAutoScrollEnabled(true);
          },
        };
        return dropDownItem;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, tableSlug]);

  const handleClearAllFilters = () => {
    setQuery('');
    tableStore.clearSortingAndFilters(tableSlug);
  };

  return {
    ...props,
    dataServiceEditorSubNav: {
      button: {
        icon: {
          asset: 'Back',
        },
        text: {
          value: t('database_page.button_texts.exit'),
        },
        onClick: routeToDatabaseHomepage,
      },
      text: {
        value: tables?.[0]?.database?.name,
      },
      searchBar: {
        textPlaceholder: t('database_page.header.search'),
        icon: {
          asset: 'Search',
        },
        // clearOnBlur: true,
        onSubmit: (val: string) => setQuery(val.trim()),
        textValue: query.trim(),
      },
    },
    tableTabNav: {
      iconDropDown: {
        toggleProps: {
          type: 'IconButton',
          button: {
            icon: {
              asset: 'Menu',
            },
          },
        },
        menuProps: {
          dropDownItems: getMappedMenuTabItems(),
        },
      },
      isLoading,
      tabsAutoScrollEnabled,
      setTabsAutoScrollEnabled,
      tableTabList: {
        tableTabItems: tables?.map((item) => {
          return {
            state: tableSlug && item.slug === tableSlug ? 'Selected' : 'Unselected',
            text: {
              value: item.name,
            },
            indicator: {},
            onClick: () => {
              if (query) {
                setQuery('');
              }
              routeToTable(item.slug);
              setTabsAutoScrollEnabled(false);
            },
          };
        }),
      },
    },
    tableBlock: { databaseSlug, tableSlug, query, handleClearAllFilters },
  };
};

export default usePresenter;
